import { NavigationContainerRef } from '@react-navigation/native';
import { DATADOG_APPLICATION_ID, DATADOG_CLIENT_TOKEN } from '@env';
import { runCallbackBasedOnCurrentEnvironment } from '../utils/runCallbackBasedOnCurrentEnvironment';
import type { RumInitConfiguration } from '@datadog/browser-rum';
import { processDatadogEvent } from './datadogEventProcessor';

export type TDataDogCommonConfig = {
  trackUserInteractions: boolean;
  trackResources: boolean;
  sessionSampleRate: number;
  environment: string;
  applicationId: string;
  clientToken: string;
  beforeSend?: RumInitConfiguration['beforeSend'];
};

export type JSONValue =
  | string
  | number
  | boolean
  | null
  | readonly JSONValue[]
  | Readonly<{ [key: string]: JSONValue }>;

export abstract class DatadogClass {
  isDatadogInitialized: boolean;
  performingActions: {
    [key: string]: {
      startTime: number;
      endTime?: number;
    };
  };
  performedActionList: {
    name: string;
    logTime: Date;
    duration: number;
  }[];
  constructor() {
    this.isDatadogInitialized = false;
    this.performingActions = {};
    this.performedActionList = [];
  }
  public async init(): Promise<void> {
    if (!this.isDatadogInitialized) {
      if (__DEV__) {
        console.debug('[Vendor] [Datadog] disabled in local environment');
        return;
      }

      if (!DATADOG_APPLICATION_ID) {
        console.error('[Vendor] [Datadog] applicationId is missing');
        return;
      }

      if (!DATADOG_CLIENT_TOKEN) {
        console.error('[Vendor] [Datadog] clientToken is missing');
        return;
      }

      const environment = runCallbackBasedOnCurrentEnvironment(
        () => 'develop', //develop callback
        () => 'staging', //staging callback
        () => 'live', //production callback
      );

      try {
        await this.initDadDog({
          trackUserInteractions: true,
          trackResources: true,
          sessionSampleRate: 100,
          environment,
          applicationId: DATADOG_APPLICATION_ID,
          clientToken: DATADOG_CLIENT_TOKEN,
          beforeSend: processDatadogEvent,
        });

        this.isDatadogInitialized = true;
      } catch (error) {
        this.isDatadogInitialized = false;
      }
    }
  }

  abstract initDadDog(commonConfig: TDataDogCommonConfig): Promise<void>;
  abstract setUserInfo(userId: string, userType: string): Promise<void>;
  abstract clearUserInfo(): Promise<void>;
  abstract setDatadogFeatureFlag(featureFlag: string, featureFlagValue: string): void;
  abstract logError(...args: any): Promise<void>;

  abstract logInfo(key: string, context: any): Promise<void>;
  abstract setGlobalAttribute(scopeKey: string, value: JSONValue): Promise<void>;
  abstract trackViews(currentView: NavigationContainerRef<any> | null): void;
  abstract startMeasure(operationName: string): void;
  abstract endMeasure(operationName: string, otherParams?: { [key: string]: number | string | boolean }): void;
  abstract cleanMeasure(operationName: string): void;
  abstract recordMeasure(
    operationName: string,
    durationMs: number,
    otherParams?: { [key: string]: number | string | boolean },
  ): void;
}
